import type { ReactNode, RefObject } from "react"
import { useCallback, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Box, alpha, useTheme } from "@mui/material"
import type { GridRowId, GridSortModel } from "@mui/x-data-grid"

import type { SxProps, Theme } from "@mui/material"

import type {
  DeviceRowsByInstallationStatus,
  InstalledOnLoading,
} from "types/device.types"
import type { InstallationTab } from "helpers/hooks/devices/useDeviceInstallationTabs"

import {
  selectDevicesTable,
  setDevicesTableRowsAmount,
  setDevicesTableSortModel,
} from "features/store/tablesSlice"
import useRoutes from "helpers/hooks/useRoutes"
import useAlertSettings from "helpers/hooks/useAlertSettings"
import {
  DEVICES_TABLE_CSV_FIELDS,
  getDeviceTableColumns,
  getDevicesTableRows,
} from "helpers/utils/devicesTable"
import { useDeviceInstallationTabs } from "helpers/hooks/devices/useDeviceInstallationTabs"
import DataGridTable from "widgets/common/DataGridTable"
import SkeletonWrapper from "widgets/common/SkeletonWrapper"
import { selectCurrentOrgName } from "features/store/orgSlice"
import { formatFilename } from "helpers/utils/common"
import DeviceInstallationTabButtons from "./DeviceInstallationTabButtons"

interface DevicesTableProps {
  devices: DeviceRowsByInstallationStatus
  isWaiting: boolean
  installedOnLoading?: InstalledOnLoading
  statusesLoading?: boolean
  titleKey?: string
  showInstallationTabs?: boolean
  fileNameEntity?: string
  sx?: SxProps<Theme>
  toolbarActions?: ReactNode
  checkboxSelection?: boolean
  selectedIds?: GridRowId[]
  onSelectionChange?: (ids: GridRowId[]) => void
  selectionActions?: ReactNode
  scrollAnchorRef?: RefObject<HTMLElement | null>
}

function DevicesTable({
  devices,
  statusesLoading,
  isWaiting,
  titleKey,
  showInstallationTabs = false,
  fileNameEntity,
  sx = {},
  installedOnLoading,
  toolbarActions,
  checkboxSelection,
  selectedIds,
  onSelectionChange,
  selectionActions,
  scrollAnchorRef,
}: DevicesTableProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const { baseOrgURL } = useRoutes()
  const { orgAlertsBySettingId } = useAlertSettings()
  const devicesTable = useSelector(selectDevicesTable)
  const orgName = useSelector(selectCurrentOrgName)
  const { activeTab, setActiveTab, filteredRows, counts } =
    useDeviceInstallationTabs(devices)
  const [pagesByTab, setPagesByTab] = useState<Record<InstallationTab, number>>({
    installed: 0,
    notInstalled: 0,
    all: 0,
  })

  const installationTabLabels = useMemo(
    () => ({
      installed: t("device_installation.DEVICES_INSTALLED"),
      notInstalled: t("device_installation.DEVICES_NOT_INSTALLED"),
      all: t("table.ALL"),
    }),
    [t],
  )

  const activeDevices = showInstallationTabs ? filteredRows : devices.all

  const csvFilename = useMemo(() => {
    const filenameParts = [
      orgName,
      fileNameEntity,
      t("generic.DEVICES"),
      showInstallationTabs ? installationTabLabels[activeTab] : undefined,
    ].filter((part): part is string => Boolean(part))

    return formatFilename(filenameParts.join("_"), "csv")
  }, [activeTab, fileNameEntity, installationTabLabels, orgName, showInstallationTabs, t])

  const columns = useMemo(() => getDeviceTableColumns(baseOrgURL, t), [baseOrgURL, t])

  const rows = useMemo(
    () =>
      getDevicesTableRows(
        activeDevices,
        orgAlertsBySettingId,
        Boolean(statusesLoading),
        installedOnLoading,
      ),
    [activeDevices, orgAlertsBySettingId, statusesLoading, installedOnLoading],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => dispatch(setDevicesTableSortModel(model)),
    [dispatch],
  )

  // Each installation tab keeps its own page, clamped to the pages that tab has.
  const pageSize = devicesTable.qtyRows ?? 10
  const page = Math.min(
    pagesByTab[activeTab],
    Math.max(0, Math.ceil(rows.length / pageSize) - 1),
  )

  const tableNode = (
    <DataGridTable
      sx={{
        // The devices table had no left padding, but for the alert status column.
        "& .MuiDataGrid-cell:not(.MuiDataGrid-cellEmpty), & .MuiDataGrid-columnHeader": {
          paddingLeft: 0,
        },
        "&& .MuiDataGrid-cellCheckbox, && .MuiDataGrid-columnHeaderCheckbox": {
          paddingLeft: 0,
        },
        "& .MuiDataGrid-cell.cell--alertStatus, & .MuiDataGrid-columnHeader.cell--alertStatus":
          {
            paddingLeft: "1.5rem",
            paddingRight: 0,
          },
        // Its 0.3rem cell padding reached the pagination row too.
        "& .MuiDataGrid-footerContainer": {
          paddingTop: "0.3rem",
          paddingBottom: "0.3rem",
        },
        "& .row--triggered .MuiDataGrid-cell": {
          backgroundColor: alpha(theme.palette.error.main, 0.1),
        },
        ...(showInstallationTabs
          ? {}
          : { border: `1px solid ${theme.palette.neutral[400]}` }),
        ...sx,
      }}
      title={titleKey}
      rowLines={2}
      rows={rows}
      columns={columns}
      csvFileName={csvFilename}
      csvFields={DEVICES_TABLE_CSV_FIELDS}
      sortModel={devicesTable.sortModel}
      onSortModelChange={handleSortModelChange}
      pageSize={pageSize}
      onPageSizeChange={(size) => {
        // Every tab's page points at other rows once the page size changes, not just this one's.
        setPagesByTab({ installed: 0, notInstalled: 0, all: 0 })
        dispatch(setDevicesTableRowsAmount(size))
      }}
      page={page}
      onPageChange={(newPage) =>
        setPagesByTab((prev) => ({ ...prev, [activeTab]: newPage }))
      }
      toolbarActions={toolbarActions}
      checkboxSelection={checkboxSelection}
      checkboxColumnWidth={56}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      selectionActions={selectionActions}
      scrollAnchorRef={scrollAnchorRef}
      gridProps={{
        columnVisibilityModel: { deviceNameCsv: false, deviceIdCsv: false },
        getRowClassName: ({ row }) => (row.alertStatus.status ? "row--triggered" : ""),
      }}
    />
  )

  return (
    <SkeletonWrapper isWaiting={isWaiting} height={540} width={"100vw"}>
      {showInstallationTabs ? (
        <Box
          sx={{
            border: `1px solid ${theme.palette.neutral[400]}`,
            borderRadius: 1,
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.neutral[400]}`,
            }}
          >
            <DeviceInstallationTabButtons
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              counts={counts}
            />
          </Box>
          {tableNode}
        </Box>
      ) : (
        tableNode
      )}
    </SkeletonWrapper>
  )
}

export default DevicesTable
